import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import studio from "../../styles/studioPage.module.css";
import adminSurface from "../../components/layouts/AdminSurface.module.css";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import TitleSubtitle from "../../components/common/TitleSubtitle";
import { useLanguage } from "../../context/useLanguage";
import { createFilter, getFilters } from "../../api/administration";
import { uploadFileToS3Complete } from "../../api/s3";
import { FilterAdminstration, GetFiltersResponse } from "../../types/requestsOutputs";
import { useGlobalState } from "../../context/useGlobalState";
import FilterEditModal from "../../components/modals/CategoryEditModal";
import { TextField, DropdownField, RadioField } from "../../components/common/FormFields";
import FormGroup from "../../components/common/FormGroup";
import { FileInput } from "../../components/common/FileInput";
import TextInput from "../../components/ui/TextInput";
import { Tooltip } from "@mui/material";
import { IMAGE_FORMAT } from "../../utils/image";
import Icon from "../../components/common/Icon";
import formStyles from "./CategoriesForm.module.css";
import stepStyles from "../track/create/createStep.module.css";

const Categories = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { data } = useGlobalState();
    const { language } = useLanguage();
    const [categories, setCategories] = useState<GetFiltersResponse>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [selectedCategory, setSelectedCategory] = useState<FilterAdminstration | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);

    useEffect(() => {
        fetchCategories();
    }, [language]);

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("Token not found");
                return;
            }
            const response = await getFilters(token);

            if (response) {
                response.forEach((filter) => {
                    // Convert boolean values to strings for display
                    if (filter.category && filter.category.categoriesAILocalized) {
                        filter.category.name =
                            filter.category.categoriesAILocalized.filter((cat) => cat.language === language)[0]?.name ||
                            filter.category.name;
                    }
                });
            }
            setCategories(response);
        } catch (error) {
            console.error("Failed to fetch cities", error);
        } finally {
            setLoading(false);
        }
    };

    const validationSchema = Yup.object({
        categoryId: Yup.string().required(t("categoryIdRequired")),
        filterName: Yup.string().required(t("filterNameRequired")),
        logoUrl: Yup.string().required(t("logoUrlRequired")),
        hasOnlyGardenStories: Yup.boolean().required(t("hasOnlyGardenStoriesRequired")),
        isDrivingMaps: Yup.boolean().required(t("isDrivingMapsRequired")),
    });

    const handleFileSelected = async (files: File | File[], setFieldValue: any) => {
        const file = Array.isArray(files) ? files[0] : files; // Toujours prendre un seul fichier
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("No authentication token found");
                return;
            }

            const result = await uploadFileToS3Complete(
                {
                    file,
                    isPublic: true, // Public files for general uploads
                    isFolder: false, // No folder structure for general uploads
                },
                token,
            );
            setFieldValue("logoUrl", result);
        } catch (err) {
            console.error("Upload error", err);
        }
    };

    const filteredCategories = categories
        .filter((category) => category.internalName?.toLowerCase().includes(search.toLowerCase()))
        .sort((a, b) => (a.internalName && b.internalName ? a.internalName.localeCompare(b.internalName) : 0));

    if (loading) return <div>{t("loading")}</div>;

    return (
        <div className={adminSurface.pageContent}>
            <div className={`${studio.pageTitle} ${adminSurface.pageTitle}`}>
                <TitleSubtitle title={t("categories")} subtitle={t("categoriesSubTitle")} />
            </div>

            <section className={adminSurface.sectionFull}>
                <h2 className={adminSurface.sectionTitle}>{t("addNewCategoryForm")}</h2>
                <p className={adminSurface.sectionHelp}>
                    {t("addNewCategoryHelp") || "La catégorie apparaîtra comme filtre dans l'application."}
                </p>

                <Formik
                    initialValues={{
                        categoryId: "",
                        filterName: "",
                        logoUrl: "",
                        hasOnlyGardenStories: false,
                        isDrivingMaps: false,
                    }}
                    validationSchema={validationSchema}
                    onSubmit={async (values, { resetForm }) => {
                        console.log("on submit", values);
                        if (!values.filterName || !values.logoUrl) return;
                        const token = localStorage.getItem("token");
                        if (!token) {
                            console.error("Token not found");
                            return;
                        }
                        console.log("on submit token", token);
                        await createFilter(
                            token,
                            values.filterName,
                            values.logoUrl,
                            values.hasOnlyGardenStories,
                            values.isDrivingMaps,
                            values.categoryId,
                        );
                        resetForm();
                        fetchCategories();
                    }}
                    validateOnMount
                >
                    {({ setFieldValue, values, isSubmitting, errors }) => (
                        <Form className={`${adminSurface.addForm} ${adminSurface.card}`}>
                            <div className={`${adminSurface.addFormGrid} ${formStyles.root}`}>
                                <div className={`${adminSurface.addField} ${adminSurface.addFieldHalf}`}>
                                    <FormGroup
                                        name="categoryId"
                                        title={t("selectACategory")}
                                        required
                                        size="small"
                                        className={stepStyles.stepDescription}
                                    >
                                        <Field
                                            name="categoryId"
                                            component={DropdownField}
                                            options={data?.categories}
                                            placeholder={t("selectACategory")}
                                            isWithoutMargin={true}
                                        />
                                    </FormGroup>
                                </div>

                                <div className={`${adminSurface.addField} ${adminSurface.addFieldHalf}`}>
                                    <FormGroup
                                        name="filterName"
                                        title={t("enterCategoryNameFilter")}
                                        required
                                        size="small"
                                        className={stepStyles.stepDescription}
                                    >
                                        <Field
                                            name="filterName"
                                            component={TextField}
                                            placeholder={t("enterCategoryNameFilter")}
                                        />
                                    </FormGroup>
                                </div>

                                <FormGroup
                                    name="logoUrl"
                                    title={t("enterCategoryLogo")}
                                    required
                                    size="small"
                                    className={stepStyles.stepDescription}
                                >
                                    <FileInput
                                        onFileSelected={(file) => handleFileSelected(file as File, setFieldValue)}
                                        acceptedFileTypes={IMAGE_FORMAT}
                                    />
                                    {values.logoUrl && <div>{t("fileUploaded")}</div>}
                                </FormGroup>

                                <FormGroup
                                    title={t("hasOnlyGardenStories")}
                                    size="small"
                                    className={stepStyles.stepDescription}
                                >
                                    <Field
                                        name="hasOnlyGardenStories"
                                        component={RadioField}
                                        value={true}
                                        label={t("hasOnlyGardenStoriesYesTitle")}
                                        subtitle={t("hasOnlyGardenStoriesYesSub")}
                                    />
                                    <Field
                                        name="hasOnlyGardenStories"
                                        component={RadioField}
                                        value={false}
                                        label={t("hasOnlyGardenStoriesNoTitle")}
                                        subtitle={t("hasOnlyGardenStoriesNoSub")}
                                    />
                                </FormGroup>

                                <FormGroup
                                    title={t("isDrivingMaps")}
                                    size="small"
                                    className={stepStyles.stepDescription}
                                >
                                    <Field
                                        name="isDrivingMaps"
                                        component={RadioField}
                                        value={true}
                                        label={t("isDrivingMapsYesTitle")}
                                        subtitle={t("isDrivingMapsYesSub")}
                                    />
                                    <Field
                                        name="isDrivingMaps"
                                        component={RadioField}
                                        value={false}
                                        label={t("isDrivingMapsNoTitle")}
                                        subtitle={t("isDrivingMapsNoSub")}
                                    />
                                </FormGroup>
                            </div>

                            <div className={adminSurface.addFoot}>
                                <Tooltip
                                    title={
                                        Object.keys(errors).length > 0
                                            ? t("formIncompleteTooltip", {
                                                  fields: Object.values(errors).join(", "),
                                              })
                                            : ""
                                    }
                                    arrow
                                >
                                    <button
                                        className={adminSurface.addButton}
                                        type="submit"
                                        disabled={isSubmitting || Object.keys(errors).length > 0}
                                    >
                                        <Icon name="ic-plus" className="ic" size={16} />
                                        {t("add")}
                                    </button>
                                </Tooltip>
                            </div>
                        </Form>
                    )}
                </Formik>
            </section>

            <div className={adminSurface.tblBar}>
                <div className={adminSurface.searchWrap}>
                    <Icon name="ic-search" className={adminSurface.searchWrapIcon} size={18} />
                    <TextInput
                        customClassName={adminSurface.searchInput}
                        placeholder={t("searchCategory")}
                        onChange={(e) => setSearch(e.target.value)}
                        value={search}
                        disabled={false}
                    />
                </div>
            </div>

            <div className={adminSurface.tableCard}>
                <div className={adminSurface.tblScrollBody}>
                    <table className={adminSurface.table}>
                        <thead>
                            <tr>
                                <th>{t("categoryName")}</th>
                                <th>{t("type")}</th>
                                <th className={adminSurface.colAct}>{t("actions")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCategories.map((lang) => (
                                <tr key={lang.id}>
                                    <td>{lang.internalName}</td>
                                    <td>{lang.category?.name}</td>
                                    <td className={adminSurface.colAct}>
                                        <div className="cities-table-actions">
                                            <button
                                                className={adminSurface.modifyButton}
                                                onClick={() => {
                                                    setSelectedCategory(lang);
                                                    setShowEditModal(true);
                                                }}
                                            >
                                                {t("modify")}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className={adminSurface.tblFoot}>
                    <span>
                        {filteredCategories.length} {t("categories")?.toLowerCase?.() || "categories"}
                    </span>
                </div>
            </div>

            {selectedCategory && (
                <FilterEditModal
                    filter={selectedCategory}
                    isOpen={showEditModal}
                    onClose={() => {
                        setShowEditModal(false);
                        fetchCategories();
                    }}
                />
            )}
        </div>
    );
};

export default Categories;
